import { useEffect, useState } from 'react'
import { Accordion, Alert, Button } from 'react-bootstrap'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { StoredOrderAnswers, StoredOrderDialog } from '@/components/forms/StoredOrderView'
import { groupOrdersByForm } from '@/components/forms/groupOrders'
import { readError } from '@/services/apiClient'
import type { CustomerOrder } from '@/services/customerService'
import { loadStoredOrder, type StoredOrder } from '@/services/formService'

function selectedKeys(key: string | string[] | null | undefined) {
  if (Array.isArray(key)) return key.map(String)
  return key ? [String(key)] : []
}

function formatWhen(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function OrderHistory({ orders, customer }: { orders: CustomerOrder[]; customer?: { accountNumber?: string | null; name: string } }) {
  const groups = groupOrdersByForm(orders)
  const [openOrders, setOpenOrders] = useState<string[]>([])
  const [viewId, setViewId] = useState<number | null>(null)
  const [revision, setRevision] = useState(0)
  if (!groups.length) return null

  return (
    <>
    <StoredOrderDialog orderId={viewId} onHide={() => setViewId(null)} onChanged={() => setRevision(value => value + 1)} />
    <Accordion className="order-history" alwaysOpen>
      {groups.map((group) => (
        <Accordion.Item key={group.formId} eventKey={String(group.formId)}>
          <Accordion.Header>
            <span>
              {group.formName}
              <span className="order-history__meta">
                {group.orders.length} {group.orders.length === 1 ? 'submission' : 'submissions'}
                {group.lastPlacedAt ? ` · Last submission ${formatWhen(group.lastPlacedAt)}` : ''}
              </span>
            </span>
          </Accordion.Header>
          <Accordion.Body>
            <Accordion
              className="order-history"
              alwaysOpen
              activeKey={openOrders}
              onSelect={(key) => setOpenOrders(selectedKeys(key))}
            >
              {group.orders.map((order) => (
                <Accordion.Item key={order.id} eventKey={String(order.id)}>
                  <Accordion.Header>
                    <span>
                      {[
                        formatWhen(order.placedAt),
                        customer?.accountNumber?.trim(),
                        order.customerName?.trim() || customer?.name,
                      ].filter(Boolean).join(' · ')}
                      <span className="order-history__meta">
                        {order.status || 'Submitted'}
                        {order.placedByName ? ` · ${order.placedByName}` : ''}
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Button size="sm" variant="outline-primary" className="mb-3" onClick={() => setViewId(order.id)}>View / edit submission</Button>
                    {openOrders.includes(String(order.id)) && <OrderSnapshot orderId={order.id} revision={`${order.dateModified ?? order.status}:${revision}`} />}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </Accordion.Body>
        </Accordion.Item>
      ))}
    </Accordion>
    </>
  )
}

function OrderSnapshot({ orderId, revision }: { orderId: number; revision: string }) {
  const [order, setOrder] = useState<StoredOrder | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    loadStoredOrder(orderId)
      .then((row) => { if (!cancelled) setOrder(row) })
      .catch((caught) => { if (!cancelled) setError(readError(caught)) })
    return () => { cancelled = true }
  }, [orderId, revision])

  if (error) return <Alert variant="danger">{error}</Alert>
  if (!order) return <LoadingSpinner text="Loading submission" size="2x" />
  return <StoredOrderAnswers order={order} />
}
